import ShippingPrivacy from '@/components/ShippingPrivacy';
import useGenerateEstString from '@/hooks/useGenerateEstString';
import { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { IRule } from '@/types/newSetup.type';
import parse from 'html-react-parser';
import { memo, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

interface IProps {
  rules: IRule[];
  ruleSelected?: IRule;
  hideSelectBox?: boolean;
  selected?: number;
  setSelected?: React.Dispatch<React.SetStateAction<number>>;
  country?: string;
  flag?: string;
  zipcode?: string;
}

const DeliveryMethodSelect = memo(function DeliveryMethodSelect(props: IProps) {
  const dataSettings = useSelector(dataSettingsSelector);
  const generateEstString = useGenerateEstString();

  const ruleSelected = useMemo(() => {
    if (props.ruleSelected) return props.ruleSelected;
    return props.rules.find((item) => item.id === props.selected) || props.rules[0];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(props.rules), props.selected]);

  const handleSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      props.setSelected?.(Number(e.target.value));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.setSelected],
  );

  const truncateName = useCallback((name: string) => (name.length > 40 ? `${name.slice(0, 38)}...` : name), []);

  const renderEstString = useMemo(() => {
    const rule = props.ruleSelected ?? ruleSelected;
    return generateEstString(
      rule?.estimatedText || '',
      rule?.minPrepDays || 0,
      rule?.maxPrepDays || 0,
      rule?.minDeliveryDays || 0,
      rule?.maxDeliveryDays || 0,
      false,
      props.country,
      props.flag,
      rule?.preOrderDate || '',
      rule?.isUsePreOrderDate,
      props.zipcode,
    );
  }, [props.ruleSelected, props.country, props.flag, props.zipcode, ruleSelected, generateEstString]);

  const selectBox = useMemo(() => {
    if (props.ruleSelected) {
      return !props.hideSelectBox ? (
        <div className="ot-estimated-shipping-shipping-methods">
          <select className="ot-estimated-shipping-shipping-methods-select-box" onChange={() => {}}>
            <option className="ot-estimated-shipping-item-shipping-method">{truncateName(props.ruleSelected.name)}</option>
          </select>
        </div>
      ) : null;
    }

    if (props.rules.length <= 1) return null;

    return (
      <div className="ot-estimated-shipping-shipping-methods">
        <select
          className="ot-estimated-shipping-shipping-methods-select-box"
          value={props.selected}
          onChange={handleSelectChange}
          style={{ border: '1px solid #ccc' }}
        >
          {props.rules.map((item) => (
            <option className="ot-estimated-shipping-item-shipping-method" value={item.id} key={item.id}>
              {truncateName(item.name)}
            </option>
          ))}
        </select>
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.ruleSelected, props.hideSelectBox, JSON.stringify(props.rules), props.selected, handleSelectChange, truncateName]);

  if (!dataSettings.enable_eta_information) return null;

  return (
    <>
      {selectBox}
      {ruleSelected && (
        <div className="ot-estimated-shipping-basic-layout">
          <span className="ot-estimated-shipping-main-text">
            {ruleSelected.isShowDeliveryMethodName && (
              <div className="ot-estimated-shipping-title-shipping">
                <span className="ot-estimated-shipping-name-shipping">
                  {parse(typeof ruleSelected.styledName === 'string' ? ruleSelected.styledName : '')}
                </span>
              </div>
            )}
            <span className="ot-estimated-shipping-main-text-estimated">{parse(renderEstString)}</span>
          </span>
          <ShippingPrivacy rule={ruleSelected} />
        </div>
      )}
    </>
  );
});

export default DeliveryMethodSelect;
